import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbToastModule } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { ToastMessage } from '@core/models/toast.model';
import { ToastService } from '@core/services/toast.service';

@Component({
  selector: 'app-toast-container',
  standalone: true,
  imports: [CommonModule, NgbToastModule],
  template: `
    <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 1200;">
      <ngb-toast
        *ngFor="let toast of toasts; trackBy: trackByToastId"
        [class]="getToastClasses(toast)"
        [autohide]="toast.autohide"
        [delay]="toast.delay"
        (shown)="onToastShown(toast)"
        (hidden)="onToastHidden(toast)"
        role="alert"
        aria-live="assertive"
        aria-atomic="true">

        <!-- Toast Header -->
        <ng-template ngbToastHeader>
          <div class="d-flex align-items-center justify-content-between w-100">
            <!-- Left side: icon + brand + title -->
            <div class="d-flex align-items-center">
              <i [class]="toast.icon + ' me-2'"
                 [class.text-success]="toast.type === 'success'"
                 [class.text-danger]="toast.type === 'danger'"
                 [class.text-warning]="toast.type === 'warning'"
                 [class.text-info]="toast.type === 'info'"></i>

              <img src="assets/images/logo-sm.png"
                   alt="brand-logo"
                   height="16"
                   class="me-2"
                   *ngIf="showBrandLogo">

              <strong class="me-auto" [class]="getTitleClasses(toast)">
                {{ toast.title }}
              </strong>
            </div>

            <!-- Right side: timestamp + close -->
            <div class="d-flex align-items-center ms-2">
              <small class="text-muted me-2" (click)="toastService.remove(toast.id)">
              </small>
            </div>
          </div>
        </ng-template>

        <!-- Toast Body -->
        <div [innerHTML]="toast.message" class="toast-message"></div>

        <!-- Progress bar -->
        <div *ngIf="toast.autohide && showProgressBar" class="toast-progress">
          <div class="toast-progress-bar"
               [class]="getProgressBarClasses(toast)"
               [style.animation-duration]="toast.delay + 'ms'"></div>
        </div>
      </ngb-toast>
    </div>
  `,
  styles: [`
    .toast-container {
      max-width: 350px;
      min-width: 300px;
    }

    .toast-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    /* Custom toast styles */
    .toast-success { border-left: 4px solid #28a745; }
    .toast-danger  { border-left: 4px solid #dc3545; }
    .toast-warning { border-left: 4px solid #ffc107; }
    .toast-info    { border-left: 4px solid #17a2b8; }

    .toast-message {
      font-size: 14px;
      line-height: 1.4;
    }

    /* Progress bar */
    .toast-progress {
      height: 3px;
      overflow: hidden;
      background-color: rgba(0, 0, 0, 0.1);
      margin: 8px -12px -12px -12px;
    }
    .toast-progress-bar {
      height: 100%;
      width: 100%;
      transform-origin: left;
      animation: toast-progress-shrink linear;
    }
    .toast-progress-success { background-color: #28a745; }
    .toast-progress-danger  { background-color: #dc3545; }
    .toast-progress-warning { background-color: #ffc107; }
    .toast-progress-info    { background-color: #17a2b8; }

    @keyframes toast-progress-shrink {
      from { transform: scaleX(1); }
      to   { transform: scaleX(0); }
    }

    /* Animation */
    .toast { animation: slideInRight 0.3s ease-out; }
    @keyframes slideInRight {
      from { opacity: 0; transform: translateX(100%); }
      to   { opacity: 1; transform: translateX(0); }
    }

    @media (max-width: 576px) {
      .toast-container {
        top: 10px !important;
        right: 10px !important;
        left: 10px !important;
        max-width: none;
      }
    }

    @media (prefers-color-scheme: dark) {
      .toast {
        background-color: #2d3748;
        border-color: #4a5568;
        color: #e2e8f0;
      }
    }
  `]
})
export class ToastContainerComponent implements OnInit, OnDestroy {
  toasts: ToastMessage[] = [];
  showBrandLogo = true;
  showProgressBar = true;
  private subscription: Subscription = new Subscription();

  constructor(public toastService: ToastService) {}

  ngOnInit(): void {
    this.subscription = this.toastService.toasts.subscribe(toasts => {
      this.toasts = toasts;
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onToastShown(_: ToastMessage): void {}
  onToastHidden(toast: ToastMessage): void {
    this.toastService.remove(toast.id);
  }

  trackByToastId(_: number, toast: ToastMessage): string {
    return toast.id;
  }

  getToastClasses(toast: ToastMessage): string {
    return `toast-${toast.type} shadow-sm`;
  }

  getTitleClasses(toast: ToastMessage): string {
    const base = 'fw-bold';
    const types: Record<string, string> = {
      success: 'text-success',
      danger: 'text-danger',
      warning: 'text-warning',
      info: 'text-info'
    };
    return `${base} ${types[toast.type]}`;
  }

  getProgressBarClasses(toast: ToastMessage): string {
    return `toast-progress-${toast.type}`;
  }

  formatTimestamp(timestamp: Date): string {
    return this.toastService.formatTimestamp(timestamp);
  }
}
